import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  TextField
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Add, Delete, Edit, ImportExport } from '@mui/icons-material';
import { TranslationExportData, useAdminApi } from '../../api/adminApi';
import { TranslationValueForm } from '../../forms/TranslationValueForm';
import { TranslationValueReq, TranslationValueResp, LanguageResp, TranslationKeyResp } from '../../types/language';
import ConfirmationDialog from '../../common/ConfirmationDialog';

interface MultiLanguageTranslationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (translations: Record<number, string>, translationKey?: string) => void;
  languages: LanguageResp[];
  translationKeys: TranslationKeyResp[];
  existingKey?: string;
}

const MultiLanguageTranslationModal: React.FC<MultiLanguageTranslationModalProps> = ({
  open,
  onClose,
  onSubmit,
  languages,
  translationKeys,
  existingKey
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [translations, setTranslations] = useState<Record<number, string>>({});
  const [selectedKey, setSelectedKey] = useState<string>(existingKey || '');
  const [isNewKey, setIsNewKey] = useState(!existingKey);

  useEffect(() => {
    if (open) {
      const initialTranslations: Record<number, string> = {};
      languages.forEach(lang => {
        initialTranslations[lang.id] = '';
      });
      setTranslations(initialTranslations);
      if (existingKey) {
        setSelectedKey(existingKey);
        setIsNewKey(false);
      } else {
        setSelectedKey('');
        setIsNewKey(true);
      }
    }
  }, [open, languages, existingKey]);

  const handleTranslationChange = (languageId: number, value: string) => {
    setTranslations(prev => ({
      ...prev,
      [languageId]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewKey && !selectedKey.trim()) {
      alert('Çeviri anahtarı boş olamaz');
      return;
    }
    onSubmit(translations, isNewKey ? selectedKey : undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Toplu Çeviri Ekleme</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {isNewKey ? (
              <TextField
                label="Yeni Çeviri Anahtarı"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                fullWidth
                required
              />
            ) : (
              <TextField
                select
                label="Mevcut Çeviri Anahtarı"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                fullWidth
                required
                SelectProps={{
                  native: true,
                }}
              >
                {translationKeys.map((key) => (
                  <option key={key.id} value={key.translationKey}>
                    {key.translationKey}
                  </option>
                ))}
              </TextField>
            )}

            <Button
              variant="outlined"
              onClick={() => setIsNewKey(!isNewKey)}
              sx={{ mb: 2 }}
            >
              {isNewKey ? 'Mevcut Anahtar Kullan' : 'Yeni Anahtar Oluştur'}
            </Button>

            {languages.map(language => (
              <TextField
                key={language.id}
                label={`${language.name} (${language.code}) Çevirisi`}
                value={translations[language.id] || ''}
                onChange={(e) => handleTranslationChange(language.id, e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>İptal</Button>
          <Button type="submit" variant="contained" color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const TranslationValueManager = () => {
  const api = useAdminApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [values, setValues] = useState<TranslationValueResp[]>([]);
  const [languages, setLanguages] = useState<LanguageResp[]>([]);
  const [keys, setKeys] = useState<TranslationKeyResp[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openMultiLanguageModal, setOpenMultiLanguageModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [currentValue, setCurrentValue] = useState<TranslationValueReq | TranslationValueResp>({
    translationId: 0,
    languageId: 0,
    translatedValue: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeyForMultiEdit, setSelectedKeyForMultiEdit] = useState<TranslationKeyResp | null>(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const [langRes, keyRes] = await Promise.all([
        api.listLanguages(),
        api.listKeys()
      ]);

      const loadedLanguages = langRes.data?.data ?? [];
      const loadedKeys = keyRes.data?.data ?? [];

      setLanguages(loadedLanguages);
      setKeys(loadedKeys);

      const valRes = await api.listValues();
      const loadedValues = valRes.data?.data ?? [];

      const processedValues = loadedValues.map(item => ({
        id: item.id,
        translationId: item.translationId,
        languageId: item.languageId,
        translatedValue: item.translatedValue,
        _translationKey: loadedKeys.find(k => k.id === item.translationId)?.translationKey || '',
        _languageName: loadedLanguages.find(l => l.id === item.languageId)?.name || ''
      }));

      setValues(processedValues);

    } catch (error) {
      console.error('Çeviri verileri yüklenirken hata:', error);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentValue({
      translationId: keys.length > 0 ? keys[0].id : 0,
      languageId: languages.length > 0 ? languages[0].id : 0,
      translatedValue: '',
    });
    setOpenModal(true);
  };

  const handleCreateMultiLanguage = () => {
    setSelectedKeyForMultiEdit(null);
    setOpenMultiLanguageModal(true);
  };
  const handleAddToAllLanguages = (key: TranslationKeyResp) => {
    setSelectedKeyForMultiEdit(key);
    setOpenMultiLanguageModal(true);
  };

  const handleEdit = (value: TranslationValueResp) => {
    setCurrentValue(value);
    setOpenModal(true);
  };

  const handleDelete = (id: number) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const submitForm = async (values: TranslationValueReq) => {
    try {
      await api.upsertValue(values);
      await fetchAll();
      setOpenModal(false);
    } catch (error) {
      console.error('Çeviri değeri kaydedilirken hata:', error);
      setError('Kayıt işlemi sırasında bir hata oluştu');
    }
  };

  const submitMultiLanguageForm = async (translations: Record<number, string>, newKey?: string) => {
    try {
      let keyId: number;

      if (newKey) {
        // Yeni anahtar oluştur
        const keyRes = await api.createKey({ translationKey: newKey });
        keyId = keyRes.data.data.id;
      } else if (selectedKeyForMultiEdit) {
        // Mevcut anahtarı kullan
        keyId = selectedKeyForMultiEdit.id;
      } else {
        throw new Error('Anahtar belirtilmedi');
      }

      // Tüm diller için çevirileri kaydet
      const promises = Object.entries(translations)
        .filter(([_, value]) => value.trim() !== '')
        .map(([languageId, value]) =>
          api.upsertValue({
            translationId: keyId,
            languageId: parseInt(languageId),
            translatedValue: value
          })
        );

      await Promise.all(promises);
      await fetchAll();
      setOpenMultiLanguageModal(false);
      setSelectedKeyForMultiEdit(null);
    } catch (error) {
      console.error('Toplu çeviri kaydında hata:', error);
      setError('Toplu kayıt işlemi sırasında bir hata oluştu');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.exportTranslations();
      const dataStr = JSON.stringify(res.data.data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

      const exportFileDefaultName = `translations_${new Date().toISOString().split('T')[0]}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } catch (error) {
      console.error('Dışa aktarma hatası:', error);
      setError('Dışa aktarma işlemi sırasında bir hata oluştu');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileReader = new FileReader();

    fileReader.onload = async (e) => {
      try {
        const content = e.target?.result;
        if (typeof content === 'string') {
          const data = JSON.parse(content) as TranslationExportData;
          await api.importTranslations(data);
          await fetchAll();
        }
      } catch (error) {
        console.error('İçe aktarma hatası:', error);
        setError('Geçersiz dosya formatı veya veri yapısı');
      }
    };

    fileReader.readAsText(file);
    event.target.value = '';
  };

  const confirmDelete = async () => {
    if (selectedId) {
      try {
        await api.deleteValue(selectedId);
        await fetchAll();
      } catch (error) {
        console.error('Çeviri değeri silinirken hata:', error);
        setError('Silme işlemi sırasında bir hata oluştu');
      } finally {
        setOpenDeleteDialog(false);
      }
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const columns: GridColDef<TranslationValueResp>[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: '_translationKey',
      headerName: 'Çeviri Anahtarı',
      flex: 1,
      minWidth: 150,
    },
    {
      field: '_languageName',
      headerName: 'Dil',
      width: 120,
    },
    {
      field: 'translatedValue',
      headerName: 'Değer',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'İşlemler',
      width: isMobile ? 150 : 200,
      getActions: (params: { row: TranslationValueResp }) => [
        <GridActionsCellItem
          key={`edit-${params.row.id}`}
          icon={<Edit />}
          label="Düzenle"
          onClick={() => handleEdit(params.row)}
          showInMenu={isMobile}
        />,
        <GridActionsCellItem
          key={`multi-edit-${params.row.id}`}
          icon={<Add />}
          label="Tüm Dillere Ekle"
          onClick={() => {
            const key = keys.find(k => k.id === params.row.translationId);
            if (key) handleAddToAllLanguages(key);
          }}
          showInMenu={isMobile}
        />,
        <GridActionsCellItem
          key={`delete-${params.row.id}`}
          icon={<Delete />}
          label="Sil"
          onClick={() => handleDelete(params.row.id)}
          showInMenu={isMobile}
        />,
      ],
    },
  ];

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" component="h2">Çeviri Değerleri</Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateMultiLanguage} // Artık parametre almıyor
            disabled={loading || languages.length === 0}
            size={isMobile ? 'small' : 'medium'}
          >
            Toplu Çeviri Ekle
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={loading || languages.length === 0 || keys.length === 0}
            size={isMobile ? 'small' : 'medium'}
          >
            Tekil Çeviri Ekle
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportExport />}
            onClick={handleExport}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
          >
            Dışa Aktar
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImportExport />}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
          >
            İçe Aktar
            <input type="file" hidden accept=".json" onChange={handleImport} />
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && languages.length === 0 && (
        <Alert severity="warning">Dil bulunamadı. Önce dil eklemelisiniz.</Alert>
      )}

      {!loading && keys.length === 0 && (
        <Alert severity="warning">Çeviri anahtarı bulunamadı. Önce anahtar eklemelisiniz.</Alert>
      )}

      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={values}
          columns={columns}
          loading={loading}
          autoHeight
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          localeText={{
            noRowsLabel: loading ? 'Yükleniyor...' : 'Kayıt bulunamadı',
            footerRowSelected: (count) =>
              `${count.toLocaleString()} satır seçildi`,
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              overflow: 'visible',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        />
      </Box>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {currentValue && 'id' in currentValue && currentValue.id
            ? 'Çeviri Değerini Düzenle'
            : 'Yeni Çeviri Değeri Ekle'}
        </DialogTitle>
        <DialogContent>
          <TranslationValueForm
            initialValues={currentValue}
            onSubmit={submitForm}
            languages={languages}
            translationKeys={keys}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>İptal</Button>
        </DialogActions>
      </Dialog>

      <MultiLanguageTranslationModal
        open={openMultiLanguageModal}
        onClose={() => {
          setOpenMultiLanguageModal(false);
          setSelectedKeyForMultiEdit(null);
        }}
        onSubmit={submitMultiLanguageForm}
        languages={languages}
        translationKeys={keys}
        existingKey={selectedKeyForMultiEdit?.translationKey}
      />

      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Çeviri Değerini Sil"
        message="Bu çeviri değerini silmek istediğinize emin misiniz?"
      />
    </Stack>
  );
};

export default TranslationValueManager;